import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import * as XLSX from 'xlsx';
import {
  getBulkBatches,
  getBatchRequests,
  saveToTranslationMemory,
  saveDraftTranslation,
  getRequestStatus,
  getBatchSourceText,
  lockTranslation,
  unlockTranslation,
} from '../api';
import { getEffectiveStatus, getStatusBucket, getStatusLabel, isAwaitingReview, isError, isTerminalStatus, isTranslated } from '../utils/status';
import { buildJsonFromLeaves, isJsonBatch } from '../utils/jsonDocument';
import RequestDetail from './RequestDetail';
import { isRequestModified } from './ModifiedBadge';
import SpanishCellPopover from './SpanishCellPopover';
import StatusCell from './StatusCell';
import TranslationLockControl from './TranslationLockControl';
import TruncatedCopyCell from './TruncatedCopyCell';
import {
  clearDraftFields,
  mergeDraftFieldsFromResponse,
  patchRequestWithDraftFields,
} from '../utils/draftState';
import {
  applyLockToRequest,
  formatBulkApproveMessage,
  formatBulkLockMessage,
  formatBulkUnlockMessage,
  isLockedByOther,
  normalizeLockState,
  normalizeUserId,
} from '../utils/translationLock';
import {
  getSourceLabel,
  getTargetLabel,
  getLanguagePairLabel,
  getTmDisplayName,
  resolveTmId,
  batchLanguageContext,
} from '../utils/languages';

const BATCHES_PER_PAGE = 15;
// Inner pagination: keeps the expanded batch view snappy when a JSON
// upload contains hundreds (or thousands) of leaf strings. Without
// this we'd render every row + every per-row tooltip + every status
// cell into the DOM at once which can freeze the browser tab.
const INNER_PAGE_SIZE = 50;

// Comparators shared with the sortable headers in the expanded view.
function compareStrings(a, b, dir) {
  const aBlank = !a;
  const bBlank = !b;
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;
  const cmp = a.toString().toLowerCase().localeCompare(b.toString().toLowerCase());
  return dir === 'asc' ? cmp : -cmp;
}

function reqSortValue(req, key, availableTMs = []) {
  switch (key) {
    case 'source':
      return req.source || req.English || '';
    case 'target':
      return req.target || req.Spanish || '';
    case 'translation_type':
      return req.translation_type || '';
    case 'status':
      return getStatusLabel(getEffectiveStatus(req));
    case 'language_pair':
      return getLanguagePairLabel(req, true);
    case 'tm_id':
      return getTmDisplayName(resolveTmId(req, availableTMs), availableTMs);
    default:
      return '';
  }
}

function getListColumnLabels(requests) {
  return {
    source: 'Source',
    target: 'Translated',
    targetLang: getTargetLabel(requests?.[0] || {}),
    sourceLang: getSourceLabel(requests?.[0] || {}),
  };
}

function formatTranslationType(type) {
  if (!type) return '-';
  return type.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join('-');
}

function BulkUploadHistory({ availableTMs = [], draftListPatchRef }) {
  const { oktaAuth, authState } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchRequests, setBatchRequests] = useState({});
  const [downloadingBatch, setDownloadingBatch] = useState(null);
  const [reviewRequest, setReviewRequest] = useState(null);
  // Outer pagination: keeps the My Translations - Files panel
  // tractable as the linguist accumulates dozens of uploads.
  const [batchPage, setBatchPage] = useState(0);
  // Per-batch sort and busy state for Approve All.
  const [innerSortKey, setInnerSortKey] = useState('idx');
  const [innerSortDir, setInnerSortDir] = useState('asc');
  const [approvingBatch, setApprovingBatch] = useState(null);
  const [approveAllConfirm, setApproveAllConfirm] = useState(null);
  // Inner page index per batch_id. Tracked separately from the outer
  // batches pagination so collapsing/re-expanding a batch keeps its
  // place. Default 0 for any batch we haven't paged in yet.
  const [innerPage, setInnerPage] = useState({});
  // Cached raw text of the originally-uploaded source file per batch.
  // Fetched lazily on first expand of a JSON batch and reused on
  // re-expand. The side viewer displays this verbatim (pretty-printed)
  // instead of reconstructing from leaves - avoids edge cases where
  // a numeric-string object key (e.g. a 9-digit client ID) would be
  // misread as an array index by a path-based rebuilder and explode
  // memory. State for each batch:
  //   undefined  - never fetched
  //   { loading: true } - in flight
  //   { text: '...' }   - loaded
  //   { error: '...' }  - failed (we render the error text in viewer)
  const [batchSource, setBatchSource] = useState({});
  // Track whether JSON viewer is expanded per batch (default: collapsed)
  const [jsonViewerExpanded, setJsonViewerExpanded] = useState({});
  const [bulkLockBusy, setBulkLockBusy] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!authState?.isAuthenticated) return;
    oktaAuth.getUser().then(setUserInfo).catch(() => setUserInfo(null));
  }, [authState, oktaAuth]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 12000);
    return () => clearTimeout(id);
  }, [toast]);

  const currentUserId = normalizeUserId(userInfo?.email);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBulkBatches();
      setBatches(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to load bulk upload history: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleExpandBatch = async (batchId, event) => {
    // Prevent any default behavior that might cause scroll
    if (event) {
      event.preventDefault();
    }
    
    // Save scroll position before state update
    const scrollY = window.scrollY;
    
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      // Restore scroll position after collapse
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
      return;
    }

    setExpandedBatch(batchId);
    
    // Restore scroll position after expand
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });

    if (!batchRequests[batchId]) {
      try {
        const requests = await getBatchRequests(batchId);
        setBatchRequests(prev => ({ ...prev, [batchId]: requests }));
      } catch (err) {
        console.error('Failed to fetch batch requests:', err);
      }
    }

    // Lazily fetch the original source file for JSON batches so the
    // side viewer can display the linguist's actual upload, not a
    // reconstruction. Excel batches don't need this - we already
    // surface the row data in the table itself.
    const expandedBatchObj = batches.find((b) => b.batch_id === batchId);
    if (expandedBatchObj && isJsonBatch(expandedBatchObj) && !batchSource[batchId]) {
      setBatchSource((prev) => ({ ...prev, [batchId]: { loading: true } }));
      try {
        const text = await getBatchSourceText(batchId);
        setBatchSource((prev) => ({ ...prev, [batchId]: { text } }));
      } catch (err) {
        console.warn('Failed to fetch source JSON for batch', batchId, err);
        setBatchSource((prev) => ({
          ...prev,
          [batchId]: { error: err.message || 'Failed to load original JSON' },
        }));
      }
    }
  };

  const handleOpenReview = useCallback(async (request) => {
    try {
      const fresh = await getRequestStatus(request.req_id);
      const merged = fresh?.draftTarget
        ? fresh
        : patchRequestWithDraftFields(fresh, request);
      setReviewRequest(merged || request);
    } catch (err) {
      console.warn('Failed to refresh request before review:', err);
      setReviewRequest(request);
    }
  }, []);

  const handleLockStatusChange = useCallback((batchId, reqId, lock) => {
    setBatchRequests((prev) => {
      const rows = prev[batchId];
      if (!rows) return prev;
      return {
        ...prev,
        [batchId]: rows.map((r) => (
          r.req_id === reqId ? applyLockToRequest(r, lock) : r
        )),
      };
    });
    setReviewRequest((prev) => (
      prev?.req_id === reqId ? applyLockToRequest(prev, lock) : prev
    ));
  }, []);

  const handleLockNotify = useCallback(({ type, text }) => {
    setToast({ type: type || 'error', text });
  }, []);

  // Lock All / Unlock All mirror Approve All: use rows already loaded in the
  // browser and call the single-row lock API per eligible row (no server-side
  // batch query on puthandler).
  const handleBulkLock = async (batch) => {
    setBulkLockBusy(`${batch.batch_id}-lock`);
    let locked = 0;
    let skippedOther = 0;
    let failed = 0;
    try {
      let rows = batchRequests[batch.batch_id];
      if (!rows || rows.length === 0) {
        rows = await getBatchRequests(batch.batch_id);
        setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: rows }));
      }

      for (const req of rows || []) {
        if (!req.req_id) {
          failed += 1;
          continue;
        }
        const state = normalizeLockState(req);
        if (state.isLocked) {
          const owner = normalizeUserId(state.lockedByUserId);
          if (owner === currentUserId) {
            continue;
          }
          skippedOther += 1;
          continue;
        }
        try {
          await lockTranslation({
            reqId: req.req_id,
            resourceType: 'file_translation',
            parentId: batch.batch_id,
          });
          locked += 1;
        } catch (err) {
          if (err.status === 409) {
            skippedOther += 1;
          } else {
            failed += 1;
          }
        }
      }

      const refreshed = await getBatchRequests(batch.batch_id);
      setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: refreshed }));

      const message = formatBulkLockMessage({ locked, skippedOther, failed });
      setToast({
        type: failed && !locked ? 'error' : 'success',
        text: message,
      });
    } catch (err) {
      setToast({ type: 'error', text: err.message || 'Bulk lock failed.' });
    } finally {
      setBulkLockBusy(null);
    }
  };

  const handleBulkUnlock = async (batch) => {
    setBulkLockBusy(`${batch.batch_id}-unlock`);
    let unlocked = 0;
    let otherLocked = 0;
    let failed = 0;
    try {
      let rows = batchRequests[batch.batch_id];
      if (!rows || rows.length === 0) {
        rows = await getBatchRequests(batch.batch_id);
        setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: rows }));
      }

      for (const req of rows || []) {
        if (!req.req_id) {
          failed += 1;
          continue;
        }
        const state = normalizeLockState(req);
        if (!state.isLocked) {
          continue;
        }
        const owner = normalizeUserId(state.lockedByUserId);
        if (owner !== currentUserId) {
          otherLocked += 1;
          continue;
        }
        try {
          await unlockTranslation({
            reqId: req.req_id,
            resourceType: 'file_translation',
            parentId: batch.batch_id,
          });
          unlocked += 1;
        } catch (err) {
          failed += 1;
        }
      }

      const refreshed = await getBatchRequests(batch.batch_id);
      setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: refreshed }));

      const message = formatBulkUnlockMessage({ unlocked, otherLocked, failed });
      setToast({
        type: failed && !unlocked ? 'error' : 'success',
        text: message,
      });
    } catch (err) {
      setToast({ type: 'error', text: err.message || 'Bulk unlock failed.' });
    } finally {
      setBulkLockBusy(null);
    }
  };

  const handleSaveDraftReview = useCallback(async (reqId, data) => {
    const response = await saveDraftTranslation({ req_id: reqId, ...data });
    const draftFields = mergeDraftFieldsFromResponse(response);
    const patchRow = (row) => (
      row && row.req_id === reqId ? patchRequestWithDraftFields(row, draftFields) : row
    );
    setReviewRequest((prev) => patchRow(prev));
    setBatchRequests((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((batchId) => {
        next[batchId] = (next[batchId] || []).map(patchRow);
      });
      return next;
    });
    return response;
  }, []);

  const batchDraftPatch = useCallback((reqId, draftFields) => {
    const patchRow = (row) => (
      row && row.req_id === reqId ? patchRequestWithDraftFields(row, draftFields) : row
    );
    setBatchRequests((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((batchId) => {
        next[batchId] = (next[batchId] || []).map(patchRow);
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!draftListPatchRef) return undefined;
    draftListPatchRef.current = batchDraftPatch;
    return () => {
      draftListPatchRef.current = null;
    };
  }, [draftListPatchRef, batchDraftPatch]);

  const handleSaveReview = useCallback(async (reqId, formData, options = {}) => {
    await saveToTranslationMemory(formData);
    if (options.keepOpen) {
      setReviewRequest((prev) => (
        prev && prev.req_id === reqId
          ? { ...prev, ...clearDraftFields() }
          : prev
      ));
    }
    if (!options.keepOpen) {
      setReviewRequest(null);
    }
    if (expandedBatch) {
      try {
        const reqs = await getBatchRequests(expandedBatch);
        setBatchRequests((prev) => ({ ...prev, [expandedBatch]: reqs }));
      } catch (err) {
        console.warn('Failed to refresh batch after review:', err);
      }
    }
  }, [expandedBatch]);

  const handleSaveAndLockReview = useCallback(async (reqId, { parentId } = {}) => {
    try {
      const result = await lockTranslation({
        reqId,
        resourceType: 'file_translation',
        parentId: parentId || expandedBatch,
      });
      if (result.warning) {
        setToast({ type: 'error', text: result.warning });
      } else {
        setToast({ type: 'success', text: 'Translation saved and locked.' });
      }
    } catch (err) {
      setToast({
        type: 'error',
        text: err.message || 'Saved to TM but lock failed.',
      });
      throw err;
    } finally {
      setReviewRequest(null);
      if (expandedBatch) {
        try {
          const reqs = await getBatchRequests(expandedBatch);
          setBatchRequests((prev) => ({ ...prev, [expandedBatch]: reqs }));
        } catch (refreshErr) {
          console.warn('Failed to refresh batch after lock:', refreshErr);
        }
      }
    }
  }, [expandedBatch]);

  // A row is reviewable when the AI translation finished. Approved
  // rows still allow re-review per the May 2 feedback.
  const canReviewRow = (req) => {
    const hasTarget = !!(req.target || req.Spanish);
    return hasTarget && isAwaitingReview(req);
  };

  const buildDownloadFilename = (batch, ext) => {
    const original = batch.file_name || `bulk-${batch.batch_id}`;
    const dot = original.lastIndexOf('.');
    const stem = dot > 0 ? original.slice(0, dot) : original;
    return `${stem}-translated.${ext}`;
  };

  // Trigger a browser download for a Blob without a server round-trip.
  // Used for the JSON download path; XLSX uses XLSX.writeFile which
  // handles this internally.
  const triggerDownloadFromBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatReviewedDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (err) {
      return '';
    }
  };

  // Client-side download: builds either an XLSX or a JSON file from
  // the loaded batch rows. For JSON batches we walk the per-request
  // source_path values and rebuild the original document tree with
  // the approved Spanish in place; the keys come straight from the
  // paths so the structure of the downloaded JSON exactly mirrors
  // what was uploaded.
  const handleDownload = async (batch) => {
    setDownloadingBatch(batch.batch_id);
    try {
      let rows = batchRequests[batch.batch_id];
      if (!rows || rows.length === 0) {
        rows = await getBatchRequests(batch.batch_id);
        setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: rows }));
      }

      const allRowsApproved = (rows || []).every(
        (r) => (r.review_status || '').toLowerCase() === 'approved'
      );
      if (!allRowsApproved) {
        const proceed = window.confirm(
          '⚠ Attention: not all translations have been approved or saved to the Translation Memory.\n\n' +
          'Do you want to continue?'
        );
        if (!proceed) {
          return;
        }
      }

      if (isJsonBatch(batch)) {
        const leaves = (rows || [])
          .filter((r) => r.source_path)
          .map((r) => ({
            source_path: r.source_path,
            value: r.target || r.Spanish || '',
          }));
        const tree = buildJsonFromLeaves(leaves);
        
        // Try pretty-printed first, fall back to compact if too large
        let jsonString;
        try {
          jsonString = JSON.stringify(tree, null, 2);
        } catch (err) {
          // If pretty-print fails (string too large), try compact format
          console.warn('Pretty-print JSON failed, trying compact format:', err.message);
          try {
            jsonString = JSON.stringify(tree);
          } catch (err2) {
            // If even compact fails, the JSON is truly too large
            throw new Error('JSON file is too large to download. Please contact support for assistance with this file.');
          }
        }
        
        const blob = new Blob([jsonString], {
          type: 'application/json',
        });
        triggerDownloadFromBlob(blob, buildDownloadFilename(batch, 'json'));
        return;
      }

      // Default Excel path: linguist-friendly columns. Per Apr/May
      // 2026 feedback we now emit:
      //  - English (source)
      //  - Spanish (Current Translation)  - the latest text (linguist edit if any, else AI)
      //  - Spanish (Original Translation) - the AI's first attempt; blank when never edited
      //  - Modified By Linguists - "Y" when current != original, blank otherwise
      // The Request ID column was dropped (linguists don't use it).
      const exportLabels = getListColumnLabels(rows);
      const currentTargetHeader = `${exportLabels.targetLang} (Current Translation)`;
      const originalTargetHeader = `${exportLabels.targetLang} (Original Translation)`;
      const sheetRows = (rows || []).map((req, idx) => {
        const current = req.target || req.Spanish || '';
        const originalAi = req.original_translation || '';
        const modified = isRequestModified(req);
        return {
          '#': idx + 1,
          [exportLabels.sourceLang]: req.source || req.English || '',
          [currentTargetHeader]: current,
          [originalTargetHeader]: originalAi,
          'Modified By Linguists': modified ? 'Y' : '',
          'Status': getStatusLabel(getEffectiveStatus(req)),
          'Match Type': formatTranslationType(req.translation_type),
          'Comments': req.notes || '',
          'Reviewed By': req.reviewed_by || '',
          'Reviewed Date': formatReviewedDate(req.reviewed_at),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(sheetRows, {
        header: [
          '#',
          exportLabels.sourceLang,
          currentTargetHeader,
          originalTargetHeader,
          'Modified By Linguists',
          'Status',
          'Match Type',
          'Comments',
          'Reviewed By',
          'Reviewed Date',
        ],
      });
      worksheet['!cols'] = [
        { wch: 5 },   // #
        { wch: 60 },  // English
        { wch: 60 },  // Spanish (Current)
        { wch: 60 },  // Spanish (Original)
        { wch: 22 },  // Modified By Linguists
        { wch: 14 },  // Status
        { wch: 16 },  // Match Type
        { wch: 40 },  // Comments
        { wch: 28 },  // Reviewed By
        { wch: 14 },  // Reviewed Date
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Translations');

      if (!allRowsApproved) {
        const disclaimerData = [
          { Notice: 'Attention: not all translations have been approved or saved to the Translation Memory.' }
        ];
        const disclaimerSheet = XLSX.utils.json_to_sheet(disclaimerData);
        disclaimerSheet['!cols'] = [{ wch: 80 }];
        XLSX.utils.book_append_sheet(workbook, disclaimerSheet, 'Disclaimer');
      }

      XLSX.writeFile(workbook, buildDownloadFilename(batch, 'xlsx'));
    } catch (err) {
      setError('Failed to download: ' + err.message);
    } finally {
      setDownloadingBatch(null);
    }
  };

  // Approve All: bulk-approves only unchanged AI translations (no edits).
  // Edited rows require individual review with Error Category and Severity.
  const handleApproveAll = async (batch) => {
    let rows = batchRequests[batch.batch_id];
    if (!rows || rows.length === 0) {
      rows = await getBatchRequests(batch.batch_id);
      setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: rows }));
    }

    const pending = (rows || []).filter((r) => {
      const hasTarget = !!(r.target || r.Spanish);
      const alreadyApproved = (r.review_status || '').toLowerCase() === 'approved';
      const current = (r.target || r.Spanish || '').trim();
      const original = (r.original_translation || current).trim();
      const unchanged = current === original;
      return hasTarget && !alreadyApproved && unchanged;
    });

    if (pending.length === 0) return;

    setApproveAllConfirm({ batch, pending });
  };

  const executeApproveAll = async () => {
    if (!approveAllConfirm) return;
    const { batch, pending } = approveAllConfirm;
    setApproveAllConfirm(null);
    setApprovingBatch(batch.batch_id);
    let approved = 0;
    let skippedLocked = 0;
    let failed = 0;
    try {
      // Row order is getBatchRequests (received_date), not the on-screen table sort.
      for (const req of pending) {
        if (isLockedByOther(normalizeLockState(req), currentUserId)) {
          skippedLocked += 1;
          continue;
        }
        const target = (req.target || req.Spanish || '').trim();
        const source = (req.source || req.English || '').trim();
        if (!target || !source) continue;
        try {
          await saveToTranslationMemory({
            source,
            target,
            sourceLanguage: req.sourceLanguage || 'en-US',
            targetLanguage: req.targetLanguage || 'es-US',
            category: req.category || 'linguist-approved',
            program: req.program || 'domestic',
            tm_id: resolveTmId(req, availableTMs),
            notes: req.notes || null,
            req_id: req.req_id,
            original_translation: req.original_translation || target,
          });
          approved += 1;
        } catch (err) {
          if (err.status === 403) {
            skippedLocked += 1;
          } else {
            failed += 1;
          }
        }
      }
      const refreshed = await getBatchRequests(batch.batch_id);
      setBatchRequests((prev) => ({ ...prev, [batch.batch_id]: refreshed }));
      setToast({
        type: failed && !approved ? 'error' : 'success',
        text: formatBulkApproveMessage({ approved, skippedLocked, failed }),
      });
    } catch (err) {
      setToast({ type: 'error', text: err.message || 'Bulk approve failed.' });
    } finally {
      setApprovingBatch(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusSummary = (batch, loadedRequests) => {
    const total = batch.total_count || (loadedRequests ? loadedRequests.length : 0) || 0;
    let completed;
    let errors;
    if (loadedRequests && loadedRequests.length > 0) {
      completed = loadedRequests.filter((r) =>
        isTranslated(r.status) || getStatusBucket(r.status) === 'approved'
      ).length;
      errors = loadedRequests.filter((r) => isError(r.status)).length;
    } else {
      completed = batch.completed_count || 0;
      errors = batch.error_count || 0;
    }

    if (errors > 0) {
      return { text: `${completed}/${total} completed, ${errors} errors`, class: 'status-warning' };
    }
    if (total > 0 && completed === total) {
      return { text: 'All completed', class: 'status-completed' };
    }
    return { text: `${completed}/${total} completed`, class: 'status-processing' };
  };

  const getReviewSummary = (batch, loadedRequests) => {
    const total = batch.total_count || (loadedRequests ? loadedRequests.length : 0) || 0;
    const reviewed = loadedRequests && loadedRequests.length > 0
      ? loadedRequests.filter((r) => (r.review_status || '').toLowerCase() === 'approved').length
      : (batch.reviewed_count || 0);

    if (reviewed === 0) return { text: 'None reviewed', class: 'review-pending' };
    if (total > 0 && reviewed === total) return { text: 'All reviewed', class: 'review-complete' };
    return { text: `${reviewed}/${total} reviewed`, class: 'review-partial' };
  };

  // Outer pagination over the batches list.
  const totalBatchPages = Math.max(1, Math.ceil(batches.length / BATCHES_PER_PAGE));
  useEffect(() => {
    if (batchPage > totalBatchPages - 1) setBatchPage(0);
  }, [batchPage, totalBatchPages]);
  const pagedBatches = useMemo(() => {
    const start = batchPage * BATCHES_PER_PAGE;
    return batches.slice(start, start + BATCHES_PER_PAGE);
  }, [batches, batchPage]);
  const batchRangeStart = batches.length === 0 ? 0 : batchPage * BATCHES_PER_PAGE + 1;
  const batchRangeEnd = Math.min((batchPage + 1) * BATCHES_PER_PAGE, batches.length);

  const handleInnerSort = (key) => {
    if (innerSortKey !== key) {
      setInnerSortKey(key);
      setInnerSortDir('asc');
      return;
    }
    if (innerSortDir === 'asc') {
      setInnerSortDir('desc');
      return;
    }
    setInnerSortKey('idx');
    setInnerSortDir('asc');
  };

  const innerSortIndicator = (key) => {
    if (innerSortKey !== key) return <span className="sort-indicator sort-indicator--idle">↕</span>;
    return (
      <span className="sort-indicator sort-indicator--active">
        {innerSortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const sortRequestsForDisplay = (requests) => {
    const indexed = requests.map((r, idx) => ({ ...r, __idx: idx }));
    if (innerSortKey === 'idx') {
      return innerSortDir === 'asc' ? indexed : [...indexed].reverse();
    }
    indexed.sort((a, b) =>
      compareStrings(
        reqSortValue(a, innerSortKey, availableTMs),
        reqSortValue(b, innerSortKey, availableTMs),
        innerSortDir,
      )
    );
    return indexed;
  };

  // Track JSON loading state per batch
  const [jsonLoading, setJsonLoading] = useState({});
  const [jsonContent, setJsonContent] = useState({});

  // Build a simple path:value format for very large documents
  // that can't be JSON.stringify'd due to string length limits
  const buildPathValueFormat = (leaves) => {
    const lines = ['// Translated JSON (path: value format)', '// Document too large for standard JSON view', ''];
    for (const leaf of leaves) {
      const path = leaf.source_path || '(unknown)';
      const value = (leaf.value || '').replace(/\n/g, '\\n');
      lines.push(`"${path}": "${value}"`);
    }
    return lines.join('\n');
  };

  // Build the translated JSON document asynchronously to avoid blocking UI
  const loadJsonContent = useCallback(async (batchId, requests) => {
    setJsonLoading(prev => ({ ...prev, [batchId]: true }));
    
    // Use setTimeout to allow UI to update with loading state
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      console.log(`Loading JSON for batch ${batchId}, ${requests.length} requests`);
      
      // Debug: log first request to see fields
      if (requests.length > 0) {
        console.log('First request fields:', Object.keys(requests[0]));
        console.log('First request source_path:', requests[0].source_path);
        console.log('First request sample:', JSON.stringify(requests[0]).substring(0, 300));
      }
      
      const leaves = requests
        .filter((r) => r.source_path)
        .map((r) => ({
          source_path: r.source_path,
          value: r.target || r.Spanish || r.source || r.English || '',
        }));
      
      console.log(`Found ${leaves.length} leaves with source_path`);
      
      if (leaves.length === 0) {
        // No source_path means this is probably an Excel file, not JSON
        setJsonContent(prev => ({ 
          ...prev, 
          [batchId]: '// No JSON data available.\n// This file may have been uploaded as a spreadsheet, not a JSON document.' 
        }));
        return;
      }
      
      // Try to build the full JSON tree
      let jsonStr;
      let tree;
      
      // Step 1: Try to build the tree structure
      try {
        tree = buildJsonFromLeaves(leaves);
        console.log('Tree built successfully, keys:', Object.keys(tree || {}).length);
      } catch (buildErr) {
        console.warn('Building JSON tree failed:', buildErr.message);
        // Fall back to path:value format
        jsonStr = buildPathValueFormat(leaves);
        setJsonContent(prev => ({ ...prev, [batchId]: jsonStr }));
        return;
      }
      
      // Step 2: Try to stringify - first pretty, then compact, then fallback
      try {
        jsonStr = JSON.stringify(tree, null, 2);
        console.log('Pretty JSON successful, length:', jsonStr?.length);
      } catch (prettyErr) {
        console.warn('Pretty JSON failed, trying compact:', prettyErr.message);
        try {
          jsonStr = JSON.stringify(tree);
          console.log('Compact JSON successful, length:', jsonStr?.length);
        } catch (compactErr) {
          console.warn('Compact JSON also failed:', compactErr.message);
          // Fall back to path:value format
          jsonStr = buildPathValueFormat(leaves);
          console.log('Using path:value format');
        }
      }
      
      // Safety check - ensure we have content
      if (!jsonStr || jsonStr === '{}' || jsonStr === '[]') {
        console.warn('JSON result is empty, using path:value format');
        jsonStr = buildPathValueFormat(leaves);
      }
      
      setJsonContent(prev => ({ ...prev, [batchId]: jsonStr }));
    } catch (err) {
      console.error('loadJsonContent error:', err);
      const errorMsg = err.message || String(err);
      setJsonContent(prev => ({ 
        ...prev, 
        [batchId]: `// Unable to build translated JSON document.\n// (${errorMsg})` 
      }));
    } finally {
      setJsonLoading(prev => ({ ...prev, [batchId]: false }));
    }
  }, []);

  // Handle JSON viewer toggle with loading
  const handleJsonViewerToggle = useCallback((batchId, requests, isCurrentlyExpanded) => {
    console.log(`handleJsonViewerToggle: batchId=${batchId}, requests=${requests?.length || 0}, expanded=${isCurrentlyExpanded}, cached=${!!jsonContent[batchId]}`);
    
    // Load content if expanding and we don't have cached content yet (or if content is empty)
    const needsLoad = !isCurrentlyExpanded && (!jsonContent[batchId] || jsonContent[batchId].length === 0);
    
    if (needsLoad && requests && requests.length > 0) {
      loadJsonContent(batchId, requests);
    } else if (needsLoad && (!requests || requests.length === 0)) {
      // Requests not loaded yet - show message
      setJsonContent(prev => ({ ...prev, [batchId]: '// Requests still loading. Please wait a moment and try again.' }));
    }
    
    setJsonViewerExpanded(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  }, [jsonContent, loadJsonContent]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-content">
          <div className="step-number">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3h18v18H3zM21 9H3M9 21V9" />
            </svg>
          </div>
          <div className="card-header-text">
            <h2>My Translations - Files</h2>
            <p>Files saved to your account and available after you sign back in</p>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="refresh-bar">
          <span className="refresh-info">
            {lastRefresh ? (
              <>Last refreshed: {lastRefresh.toLocaleTimeString()}</>
            ) : (
              'Not yet loaded'
            )}
            {batches.length > 0 && (
              <>{' · '}Showing {batchRangeStart}-{batchRangeEnd} of {batches.length}</>
            )}
          </span>
          <button
            className="btn btn-secondary btn-small"
            onClick={fetchBatches}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Refreshing...
              </>
            ) : (
              '↻ Refresh'
            )}
          </button>
        </div>

        {error && (
          <div className="error-banner">{error}</div>
        )}

        {batches.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-state-icon">📁</div>
            <p>No file translations yet.</p>
            <p>Upload a spreadsheet in the "Translate Files" tab to get started.</p>
          </div>
        ) : (
        <div className="batch-list">
          {pagedBatches.map((batch) => {
            const isExpanded = expandedBatch === batch.batch_id;
            const requests = batchRequests[batch.batch_id] || [];
            const statusSummary = getStatusSummary(batch, requests);
            const reviewSummary = getReviewSummary(batch, requests);

            // Download is gated behind "all translations approved"
            // - the batch must be expanded at least once so we have
            // the row data to evaluate. Until then we keep download
            // disabled with a hint.
            const hasLoadedRows = requests.length > 0;
            const allApproved = hasLoadedRows
              && requests.every((r) => (r.review_status || '').toLowerCase() === 'approved');
            // Check if all translations have completed (not still processing)
            const allTranslationsComplete = hasLoadedRows && requests.every((r) => isTerminalStatus(r.status));
            const downloadDisabled =
              downloadingBatch === batch.batch_id || !hasLoadedRows;

            const downloadTitle = !hasLoadedRows
              ? 'Expand the batch to load translations first'
              : !allApproved
                ? 'Download file (some translations not yet approved)'
                : 'Download translated file';

            // Inner pagination computed per-batch so collapsing one
            // doesn't reset another. Sorting happens before paging
            // so the page reflects the active sort.
            const sortedRequests = hasLoadedRows ? sortRequestsForDisplay(requests) : [];
            const innerTotalPages = Math.max(1, Math.ceil(sortedRequests.length / INNER_PAGE_SIZE));
            const currentInnerPage = Math.min(innerPage[batch.batch_id] || 0, innerTotalPages - 1);
            const innerPageStart = currentInnerPage * INNER_PAGE_SIZE;
            const innerPageEnd = Math.min(innerPageStart + INNER_PAGE_SIZE, sortedRequests.length);
            const innerPageRows = sortedRequests.slice(innerPageStart, innerPageEnd);
            const innerRangeStart = sortedRequests.length === 0 ? 0 : innerPageStart + 1;
            const batchCtx = batchLanguageContext(batch, availableTMs)
              || (hasLoadedRows ? {
                tmName: getTmDisplayName(resolveTmId(sortedRequests[0], availableTMs), availableTMs),
                pairLabel: getLanguagePairLabel(sortedRequests[0]),
              } : null);
            const innerColumnLabels = hasLoadedRows ? getListColumnLabels(sortedRequests) : getListColumnLabels([]);

            return (
              <div key={batch.batch_id} className="batch-card">
                <div className="batch-header" onClick={(e) => handleExpandBatch(batch.batch_id, e)}>
                  <div className="batch-info">
                    <div className="batch-name">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      {batch.file_name || 'Bulk Upload'}
                    </div>
                    <div className="batch-meta">
                      <span>{formatDate(batch.created_at)}</span>
                      <span>·</span>
                      <span>
                        {batch.total_count} translations
                        {requests.length > 0 && (() => {
                          const totalWords = requests.reduce((sum, r) => {
                            if (r.source_word_count != null) return sum + Number(r.source_word_count);
                            const src = r.source || r.English || '';
                            return sum + (src ? src.split(/\s+/).filter(Boolean).length : 0);
                          }, 0);
                          return totalWords > 0 ? ` · ${totalWords.toLocaleString()} words` : '';
                        })()}
                      </span>
                      <span>·</span>
                      <span>By {batch.submitted_by || 'Unknown'}</span>
                      {batchCtx && (
                        <>
                          <span>·</span>
                          <span title={batchCtx.tmId}>{batchCtx.tmName}</span>
                          <span>·</span>
                          <span>{batchCtx.pairLabel}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="batch-status">
                    <span className={`status-badge ${statusSummary.class}`}>
                      {statusSummary.text}
                    </span>
                    <span className={`review-badge ${reviewSummary.class}`}>
                      {reviewSummary.text}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="batch-details">
                    {requests.length === 0 ? (
                      <div className="loading-requests">
                        <span className="spinner"></span> Loading translations...
                      </div>
                    ) : (
                      <div className="batch-details-stacked">
                        {/* Table + pagination + footer actions stacked vertically */}
                        <div className="batch-content-column">
                          <div className="batch-table-scroll batch-table-scroll--fit-viewport">
                          <table className="data-table batch-requests-table data-table--compact">
                            <thead>
                              <tr>
                                <th className="col-row-num">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('idx')}>
                                    # {innerSortIndicator('idx')}
                                  </button>
                                </th>
                                <th className="col-language">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('language_pair')}>
                                    Language {innerSortIndicator('language_pair')}
                                  </button>
                                </th>
                                <th className="col-tm-header">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('tm_id')}>
                                    TM {innerSortIndicator('tm_id')}
                                  </button>
                                </th>
                                <th className="col-source">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('source')}>
                                    {innerColumnLabels.source} {innerSortIndicator('source')}
                                  </button>
                                </th>
                                <th className="col-target">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('target')}>
                                    {innerColumnLabels.target} {innerSortIndicator('target')}
                                  </button>
                                </th>
                                <th className="col-match-type">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('translation_type')}>
                                    Match Type {innerSortIndicator('translation_type')}
                                  </button>
                                </th>
                                <th className="col-status">
                                  <button type="button" className="th-sort" onClick={() => handleInnerSort('status')}>
                                    Status {innerSortIndicator('status')}
                                  </button>
                                </th>
                                <th className="col-action">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {innerPageRows.map((req) => (
                                <tr
                                  key={req.req_id}
                                  className={isRequestModified(req) ? 'row-modified' : ''}
                                >
                                  <td>{req.__idx + 1}</td>
                                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                    {getLanguagePairLabel(req, true)}
                                  </td>
                                  <td
                                    className="text-wrap"
                                    style={{ fontSize: '0.85rem', maxWidth: '120px' }}
                                    title={resolveTmId(req, availableTMs)}
                                  >
                                    {getTmDisplayName(resolveTmId(req, availableTMs), availableTMs) || '—'}
                                  </td>
                                  <td className="text-wrap col-source">
                                    <TruncatedCopyCell
                                      value={req.source || ''}
                                      displayText={
                                        (req.source || '').substring(0, 48)
                                        + ((req.source || '').length > 48 ? '...' : '')
                                      }
                                      copyAriaLabel="Copy Source"
                                      tooltipWrap
                                    />
                                  </td>
                                  <td className="text-wrap col-target">
                                    <SpanishCellPopover
                                      request={req}
                                      displayText={
                                        (req.target || '-').substring(0, 48)
                                        + ((req.target || '').length > 48 ? '...' : '')
                                      }
                                    />
                                  </td>
                                  <td>
                                    <span className={`type-badge type-${req.translation_type || 'pending'}`}>
                                      {formatTranslationType(req.translation_type)}
                                    </span>
                                  </td>
                                  <td className="col-status">
                                    <StatusCell request={req} />
                                  </td>
                                  <td className="col-action">
                                    {canReviewRow(req) ? (
                                      <div className="action-controls">
                                        <button
                                          className="btn btn-primary btn-small btn-nowrap"
                                          onClick={() => handleOpenReview(req)}
                                          title="Review and approve this translation"
                                        >
                                          Review Translation
                                        </button>
                                        <TranslationLockControl
                                          resourceType="file_translation"
                                          translationId={req.req_id}
                                          parentId={batch.batch_id}
                                          currentUserId={currentUserId}
                                          {...normalizeLockState(req)}
                                          onLockStatusChange={(lock) => {
                                            handleLockStatusChange(batch.batch_id, req.req_id, lock);
                                          }}
                                          onNotify={handleLockNotify}
                                        />
                                      </div>
                                    ) : (
                                      <span className="action-placeholder">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {innerTotalPages > 1 && (
                          <div className="pagination-bar pagination-bar--inner">
                            <button
                              type="button"
                              className="btn btn-ghost btn-small"
                              onClick={() =>
                                setInnerPage((prev) => ({
                                  ...prev,
                                  [batch.batch_id]: Math.max(0, currentInnerPage - 1),
                                }))
                              }
                              disabled={currentInnerPage === 0}
                            >
                              ← Previous
                            </button>
                            <span className="pagination-info">
                              Showing {innerRangeStart}-{innerPageEnd} of {sortedRequests.length}
                              {' · '}Page {currentInnerPage + 1} of {innerTotalPages}
                            </span>
                            <button
                              type="button"
                              className="btn btn-ghost btn-small"
                              onClick={() =>
                                setInnerPage((prev) => ({
                                  ...prev,
                                  [batch.batch_id]: Math.min(innerTotalPages - 1, currentInnerPage + 1),
                                }))
                              }
                              disabled={currentInnerPage >= innerTotalPages - 1}
                            >
                              Next →
                            </button>
                          </div>
                        )}

                        <div className="batch-footer-actions">
                          <button
                            className="btn btn-success btn-small"
                            onClick={() => handleApproveAll(batch)}
                            disabled={approvingBatch === batch.batch_id || allApproved || !allTranslationsComplete}
                            title={!allTranslationsComplete
                              ? 'Wait for all translations to complete before approving'
                              : allApproved
                                ? 'All translations are already approved'
                                : 'Mark all remaining translations as approved'}
                          >
                            {approvingBatch === batch.batch_id ? (
                              <>
                                <span className="spinner"></span>
                                Approving...
                              </>
                            ) : (
                              allApproved ? '✓ All Approved' : 'Approve All'
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            onClick={() => handleBulkLock(batch)}
                            disabled={bulkLockBusy === `${batch.batch_id}-lock` || !allTranslationsComplete}
                            title={!allTranslationsComplete
                              ? 'Wait for all translations to complete before locking'
                              : 'Lock all unlocked translations in this file'}
                          >
                            {bulkLockBusy === `${batch.batch_id}-lock` ? (
                              <>
                                <span className="spinner"></span>
                                Locking...
                              </>
                            ) : (
                              'Lock All'
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            onClick={() => handleBulkUnlock(batch)}
                            disabled={bulkLockBusy === `${batch.batch_id}-unlock` || !allTranslationsComplete}
                            title={!allTranslationsComplete
                              ? 'Wait for all translations to complete before unlocking'
                              : 'Unlock translations you have locked in this file'}
                          >
                            {bulkLockBusy === `${batch.batch_id}-unlock` ? (
                              <>
                                <span className="spinner"></span>
                                Unlocking...
                              </>
                            ) : (
                              'Unlock All'
                            )}
                          </button>
                          <button
                            className="btn btn-secondary btn-small"
                            onClick={() => handleDownload(batch)}
                            disabled={downloadDisabled}
                            title={downloadTitle}
                          >
                            {downloadingBatch === batch.batch_id ? (
                              <>
                                <span className="spinner"></span>
                                Downloading...
                              </>
                            ) : (
                              '↓ Download'
                            )}
                          </button>
                        </div>
                        </div>

                        {/* Collapsible translated JSON viewer below the table for JSON batches */}
                        {isJsonBatch(batch) && (
                          <div className={`json-viewer-container ${jsonViewerExpanded[batch.batch_id] ? 'json-viewer-container--expanded' : ''}`}>
                            <button 
                              type="button"
                              className="json-viewer-toggle-btn"
                              onClick={() => handleJsonViewerToggle(batch.batch_id, requests, jsonViewerExpanded[batch.batch_id])}
                            >
                              <span className="json-viewer-toggle-icon">
                                {jsonLoading[batch.batch_id] ? (
                                  <span className="spinner spinner--small"></span>
                                ) : jsonViewerExpanded[batch.batch_id] ? '▼' : '▶'}
                              </span>
                              <span className="json-viewer-toggle-text">
                                {jsonLoading[batch.batch_id] 
                                  ? 'Loading JSON...' 
                                  : jsonViewerExpanded[batch.batch_id] 
                                    ? 'Hide Translated JSON' 
                                    : 'Show Translated JSON'}
                              </span>
                              <span className="json-viewer-toggle-hint">
                                Translated values with original keys preserved
                              </span>
                            </button>
                            {jsonViewerExpanded[batch.batch_id] && (
                              <aside className="json-viewer json-viewer--below" aria-label="Translated JSON document">
                                {jsonLoading[batch.batch_id] ? (
                                  <div className="json-viewer-loading">
                                    <span className="spinner"></span>
                                    <span>Building translated JSON... This may take a moment for large files.</span>
                                  </div>
                                ) : (
                                  <pre className="json-viewer-body json-viewer-body--scrollable">
                                    {jsonContent[batch.batch_id] || '// Loading...'}
                                  </pre>
                                )}
                              </aside>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        {totalBatchPages > 1 && (
          <div className="pagination-bar">
            <button
              className="btn btn-ghost btn-small"
              onClick={() => setBatchPage((p) => Math.max(0, p - 1))}
              disabled={batchPage === 0}
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {batchPage + 1} of {totalBatchPages}
            </span>
            <button
              className="btn btn-ghost btn-small"
              onClick={() => setBatchPage((p) => Math.min(totalBatchPages - 1, p + 1))}
              disabled={batchPage >= totalBatchPages - 1}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {reviewRequest && (
        <RequestDetail
          key={`${reviewRequest.req_id}-${reviewRequest.draftEditedAt || 'none'}`}
          request={reviewRequest}
          onClose={() => setReviewRequest(null)}
          onSave={handleSaveReview}
          onSaveDraft={handleSaveDraftReview}
          onSaveAndLock={handleSaveAndLockReview}
          onNotifyLockWarning={(text) => setToast({ type: 'error', text })}
          availableTMs={availableTMs}
          readOnly={isLockedByOther(normalizeLockState(reviewRequest), currentUserId)}
          lockInfo={normalizeLockState(reviewRequest)}
          resourceType="file_translation"
          parentId={reviewRequest.batch_id || expandedBatch}
        />
      )}

      {toast && (
        <div className={`toast toast-${toast.type}`} role="status" aria-live="polite">
          <span className="toast-icon" aria-hidden="true">
            {toast.type === 'error' ? '!' : '✓'}
          </span>
          <span className="toast-text">{toast.text}</span>
          <button
            className="toast-close"
            onClick={() => setToast(null)}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      )}

      {approveAllConfirm && (
        <div className="modal-overlay" onClick={() => setApproveAllConfirm(null)}>
          <div className="modal modal--compact" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Approve All</h2>
              <button className="modal-close" onClick={() => setApproveAllConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>
                You are about to approve all unchanged translations in this file.
                Rows locked by other users will be skipped. Are you sure you want to proceed?
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setApproveAllConfirm(null)}
              >
                No, cancel this action
              </button>
              <button
                className="btn btn-success"
                onClick={executeApproveAll}
              >
                Yes, approve all and save to translation memory
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkUploadHistory;
